import React, {
  useState,
  forwardRef,
  useEffect,
  PropsWithChildren,
  ReactNode,
  cloneElement,
} from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import SwiperClass from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { useUniqueId } from "../hooks/useUniqueId";
import {
  ReactComponentStaticProps,
  ReactDefaultPropsWithChildren,
} from "../../types/react";
import { SliderButton } from "../SliderButton";
import { ImageSlide } from "./ImageSlide";
import { VideoSlide } from "./VideoSlide";
import { IframeSlide } from "./IframeSlide";

import styles from "./ProductImage.scss";

export type DeprecatedProductImageProps = {
  images?: {
    src: string;
    alt: string;

    onClick?: ((e: React.MouseEvent<HTMLButtonElement>) => void) | null;
  }[];
};

type ProductImageBaseProps = {
  thumbs?: { src: string; alt: string; key?: string }[];
  customClass?: string;
} & DeprecatedProductImageProps;

export type ProductImageProps = PropsWithChildren<ProductImageBaseProps>;

const defaultProps = {
  children: undefined,
  images: undefined,
  thumbs: undefined,
  customClass: undefined,
} as const;

const validatedDefaultProps: ReactDefaultPropsWithChildren<
  ProductImageBaseProps,
  typeof defaultProps
> = defaultProps;

const propTypes: ReactComponentStaticProps<ProductImageProps>["propTypes"] = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  images: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
      onClick: PropTypes.func,
    }).isRequired
  ),
  thumbs: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
    }).isRequired
  ),
  customClass: PropTypes.string,
} as const;

const ProductImageWithRef = (
  props: ProductImageProps,
  forwardedRef: React.ComponentPropsWithRef<"div">["ref"]
) => {
  const [swiperInstance, setSwiperInstance] = useState<SwiperClass>();
  const [activeIndex, setActiveIndex] = useState(0);
  const [reachedStart, setReachedStart] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const { children, images, thumbs, customClass, ...restProps } = props;

  const uid = useUniqueId("ProductImage", props);

  const swiperConfig: Swiper = {
    slidesPerView: 1,
    navigation: {
      prevEl: `#prev-button-${uid}`,
      nextEl: `#next-button-${uid}`,
    },
    keyboard: {
      enabled: true,
    },
    onSlideChange: (swiper: SwiperClass) => {
      setActiveIndex(swiper.activeIndex);
      setReachedEnd(swiper.isEnd);
      setReachedStart(swiper.isBeginning);
    },
    onSwiper: (swiper: SwiperClass) => {
      setSwiperInstance(swiper);
    },
    watchOverflow: true,
    centeredSlides: true,
  };

  // TODO: Remove once deprecated props are removed
  const slidesFromDeprecatedProps = images?.map((image, index) => {
    const { src, alt, onClick, ...deprecatedRestProps } = image;
    return (
      // eslint-disable-next-line react/no-array-index-key
      <SwiperSlide key={`deprecated-slide-${index}`} className="swiper-slide">
        <button
          type="button"
          className={cx(styles.slide, { [styles.clickable]: onClick })}
          onClick={(e) => {
            if (onClick) onClick(e);
          }}
          tabIndex={activeIndex === index && onClick ? 0 : -1}
          {...deprecatedRestProps}
        >
          <img src={src} alt={alt} className={styles.image} />
        </button>
      </SwiperSlide>
    );
  });

  useEffect(() => {
    if (images) {
      // eslint-disable-next-line no-console
      console.warn(
        '"images" prop is deprecated. Use compound components as children instead.'
      );
    }
  }, [images]);

  const shouldRenderAsSlider =
    (images?.length || 0) > 1 || React.Children.count(children) > 1;

  return (
    <div
      className={cx(styles.wrapper, customClass)}
      ref={forwardedRef}
      {...restProps}
    >
      <div className={styles["slider-container"]}>
        <SliderButton
          customClass={styles["slider-button"]}
          variant="backwards"
          id={`prev-button-${uid}`}
          disabled={reachedStart || !shouldRenderAsSlider}
        />

        <Swiper {...swiperConfig}>
          {slidesFromDeprecatedProps}
          {React.Children.map<ReactNode, ReactNode>(
            children,
            (child, index) => {
              if (child) {
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <SwiperSlide key={`swiper-slide-${index}`}>
                    {cloneElement((child as unknown) as any, {
                      active: activeIndex === index,
                      key: `slide-child-${index}`,
                    })}
                  </SwiperSlide>
                );
              }
              return null;
            }
          )}
        </Swiper>

        <SliderButton
          customClass={styles["slider-button"]}
          variant="forwards"
          id={`next-button-${uid}`}
          disabled={reachedEnd || !shouldRenderAsSlider}
        />
      </div>

      {shouldRenderAsSlider && (
        <div className={styles.thumbnails}>
          {thumbs?.map((thumb, index) => (
            <button
              type="button"
              className={cx(styles["thumbnail-wrapper"], {
                [styles["thumbnail-wrapper--active"]]: activeIndex === index,
              })}
              key={`thumbnail-${thumb.key || index}`}
              onClick={() => {
                swiperInstance?.slideTo(index);
              }}
            >
              <img
                src={thumb.src}
                alt={thumb.alt}
                className={styles.thumbnail}
                draggable={false}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const ProductImage = (forwardRef(ProductImageWithRef) as unknown) as ((
  props: ProductImageProps
) => ReturnType<typeof ProductImageWithRef>) &
  ReactComponentStaticProps<ProductImageProps> & {
    ImageSlide: typeof ImageSlide;
    VideoSlide: typeof VideoSlide;
    IframeSlide: typeof IframeSlide;
  };

ProductImage.displayName = "ProductImage";

ProductImage.ImageSlide = ImageSlide;
ProductImage.VideoSlide = VideoSlide;
ProductImage.IframeSlide = IframeSlide;

ProductImage.propTypes = propTypes;
ProductImage.defaultProps = validatedDefaultProps;

export default ProductImage;
